import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import * as XLSX from 'xlsx';
import { ClientIInfo } from '../../../../../../core/models/parking.model';
import {
  IdNameOption,
  ImportParkingCardPreviewRow,
  ImportParkingCardRowResult,
  ImportParkingCardsPayload,
  ImportParkingCardsResponse,
  SubscriptionInfoDto,
} from '../../../../../../core/models/parking/parking-card-list.model';
import { ApiParkingCardListService } from '../../../../../../core/services/api/parking/api-parking-card-list.service';
import { ApiService } from '../../../../../../core/services/api.service';
import { MenuPermissionService } from '../../../../../../core/services/menu-permission.service';

@Component({
  selector: 'app-modal-parking-card-import',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './modal-parking-card-import.component.html',
  styleUrl: './modal-parking-card-import.component.css',
})
export class ModalParkingCardImportComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() siteId = 0;
  @Input() siteName = '';
  @Input() userId = 0;
  @Output() onClose = new EventEmitter<void>();
  @Output() onImported = new EventEmitter<void>();

  private readonly api = inject(ApiParkingCardListService);
  private readonly apiLegacy = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  step: 1 | 2 | 3 = 1;

  clients: ClientIInfo[] = [];
  cardTypes: IdNameOption[] = [];
  subscriptions: IdNameOption[] = [];
  provinces: IdNameOption[] = [];

  clientId = 0;
  clientSearchText = '';
  isClientDropdownOpen = false;
  cardTypeId = 0;
  searchCardTypeText = '';
  isCardTypeDropdownOpen = false;
  subscriptionId = 0;
  searchSubscriptionText = '';
  isSubscriptionDropdownOpen = false;
  cardMode = 1;
  rateId = 0;
  gateGroupId = 0;
  issueDate = '';
  expireDate = '';

  previewRows: ImportParkingCardPreviewRow[] = [];
  selectedFileName = '';
  isImporting = false;
  importResult: ImportParkingCardsResponse | null = null;

  readonly cardModeOptions = [
    { value: 1, label: 'ปกติ' },
    { value: 3, label: 'ถูกระงับ' },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.resetWizard();
      this.loadMasters();
    }
  }

  get filteredClients(): ClientIInfo[] {
    const q = this.clientSearchText.trim().toLowerCase();
    const sorted = [...this.clients].sort((a, b) => a.name.localeCompare(b.name, 'th'));
    if (!q) return sorted.slice(0, 30);
    return sorted.filter((c) => c.name.toLowerCase().includes(q));
  }

  get filteredCardTypes(): IdNameOption[] {
    const q = this.searchCardTypeText.trim().toLowerCase();
    if (!q) return this.cardTypes;
    return this.cardTypes.filter((c) => c.name.toLowerCase().includes(q));
  }

  get filteredSubscriptions(): IdNameOption[] {
    const q = this.searchSubscriptionText.trim().toLowerCase();
    if (!q) return this.subscriptions;
    return this.subscriptions.filter((s) => s.name.toLowerCase().includes(q));
  }

  get selectedClientName(): string {
    return this.clients.find((c) => c.id === this.clientId)?.name ?? '-';
  }

  get selectedCardTypeName(): string {
    return this.cardTypes.find((c) => c.id === this.cardTypeId)?.name ?? '-';
  }

  get selectedSubscriptionName(): string {
    return this.subscriptions.find((s) => s.id === this.subscriptionId)?.name ?? '-';
  }

  get isStep1Valid(): boolean {
    return (
      this.siteId > 0 &&
      this.userId > 0 &&
      this.clientId > 0 &&
      this.cardTypeId > 0 &&
      this.subscriptionId > 0 &&
      this.rateId > 0 &&
      this.gateGroupId > 0 &&
      !!this.issueDate &&
      !!this.expireDate &&
      this.expireDate >= this.issueDate
    );
  }

  get validPreviewCount(): number {
    return this.previewRows.filter((r) => r.ok).length;
  }

  get errorPreviewCount(): number {
    return this.previewRows.filter((r) => !r.ok).length;
  }

  get canGoStep2(): boolean {
    return this.isStep1Valid;
  }

  get canGoStep3(): boolean {
    return this.validPreviewCount > 0;
  }

  resetWizard(): void {
    this.step = 1;
    this.clientId = 0;
    this.clientSearchText = '';
    this.cardTypeId = 0;
    this.searchCardTypeText = '';
    this.subscriptionId = 0;
    this.searchSubscriptionText = '';
    this.cardMode = 1;
    this.rateId = 0;
    this.gateGroupId = 0;
    const today = new Date();
    const nextYear = new Date(today);
    nextYear.setFullYear(today.getFullYear() + 1);
    this.issueDate = today.toISOString().slice(0, 10);
    this.expireDate = nextYear.toISOString().slice(0, 10);
    this.previewRows = [];
    this.selectedFileName = '';
    this.isImporting = false;
    this.importResult = null;
  }

  loadMasters(): void {
    if (!this.siteId) return;

    this.apiLegacy.GetClientIInfo(this.siteId).subscribe({
      next: (res) => (this.clients = res ?? []),
      error: () => (this.clients = []),
    });

    this.api.getParkingCardTypes().subscribe({
      next: (res) => (this.cardTypes = res ?? []),
      error: () => (this.cardTypes = []),
    });

    this.api.getParkingSubscriptionDropdown(this.siteId).subscribe({
      next: (res) => (this.subscriptions = res?.data ?? []),
      error: () => (this.subscriptions = []),
    });

    this.api.getParkingProvinces().subscribe({
      next: (res) => (this.provinces = res ?? []),
      error: () => (this.provinces = []),
    });
  }

  selectClient(client: ClientIInfo): void {
    this.clientId = client.id;
    this.clientSearchText = client.name;
    this.isClientDropdownOpen = false;
  }

  selectCardType(item: IdNameOption): void {
    this.cardTypeId = item.id;
    this.searchCardTypeText = item.name;
    this.isCardTypeDropdownOpen = false;
  }

  selectSubscription(item: IdNameOption): void {
    this.subscriptionId = item.id;
    this.searchSubscriptionText = item.name;
    this.isSubscriptionDropdownOpen = false;
    this.rateId = 0;
    this.gateGroupId = 0;
    this.api.getSubscriptionInfoById(item.id).subscribe({
      next: (info) => {
        const raw = info as SubscriptionInfoDto & Record<string, unknown>;
        this.rateId = Number(info?.rateId ?? raw['RateId'] ?? 0);
        this.gateGroupId = Number(info?.gateGroupId ?? raw['GateGroupId'] ?? 0);
        if (this.rateId <= 0 || this.gateGroupId <= 0) {
          this.toast.error('Subscription นี้ไม่มี rate/gate group');
        }
      },
      error: () => {
        this.rateId = 0;
        this.gateGroupId = 0;
        this.toast.error('โหลดข้อมูล Subscription ไม่สำเร็จ');
      },
    });
  }

  goNext(): void {
    if (this.step === 1) {
      if (!this.canGoStep2) {
        this.toast.error('กรุณาเลือกตั้งค่าบัตรให้ครบก่อน');
        return;
      }
      this.step = 2;
      return;
    }
    if (this.step === 2) {
      if (!this.canGoStep3) {
        this.toast.error('ไม่มีแถวที่พร้อม import');
        return;
      }
      this.step = 3;
      this.importResult = null;
    }
  }

  goBack(): void {
    if (this.step === 2) this.step = 1;
    else if (this.step === 3) this.step = 2;
  }

  downloadSampleExcel(): void {
    const sampleData = [
      {
        licensePlate: 'กข1234',
        province: 'กรุงเทพมหานคร',
        name: 'สมชาย ใจดี',
        phone: '0812345678',
        address: '123 ถ.พระราม 9 กรุงเทพฯ',
      },
      {
        licensePlate: '1กก9999',
        province: 'นนทบุรี',
        name: 'สมหญิง รักดี',
        phone: '0898765432',
        address: '99 หมู่ 1 ต.บางกรวย',
      },
    ];
    const headers = ['licensePlate', 'province', 'name', 'phone', 'address'];
    const worksheet = XLSX.utils.json_to_sheet(sampleData, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'ParkingCards');
    XLSX.writeFile(workbook, 'ParkingCards_Import_Template.xlsx');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    if (!this.isStep1Valid) {
      this.toast.error('กรุณาตั้งค่าบัตรร่วมให้ครบก่อนอัปโหลด');
      input.value = '';
      return;
    }

    const file = input.files[0];
    this.selectedFileName = file.name;
    this.importResult = null;

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
        this.previewRows = this.mapAndValidateRows(rows);
        if (!this.previewRows.length) {
          this.toast.error('ไม่พบข้อมูลในไฟล์ Excel');
        }
      } catch (err) {
        console.error(err);
        this.toast.error('อ่านไฟล์ Excel ไม่สำเร็จ');
        this.previewRows = [];
      }
    };
    reader.readAsArrayBuffer(file);
    input.value = '';
  }

  private mapAndValidateRows(rows: Record<string, unknown>[]): ImportParkingCardPreviewRow[] {
    const seenPlates = new Set<string>();
    return rows.map((row, index) => {
      const licensePlate = this.normalizeLicensePlate(
        String(row['licensePlate'] ?? row['LicensePlate'] ?? ''),
      );
      const province = String(row['province'] ?? row['Province'] ?? '').trim();
      const name = String(row['name'] ?? row['Name'] ?? '').trim();
      const phone = String(row['phone'] ?? row['Phone'] ?? '').trim();
      const address = String(row['address'] ?? row['Address'] ?? '').trim();
      const provinceId = this.resolveProvinceId(province);

      let ok = true;
      let error = '';

      if (!name) {
        ok = false;
        error = 'ชื่อว่าง';
      } else if (!licensePlate) {
        ok = false;
        error = 'ทะเบียนว่าง';
      } else if (!province) {
        ok = false;
        error = 'จังหวัดว่าง';
      } else if (provinceId <= 0) {
        ok = false;
        error = `ไม่พบจังหวัด "${province}"`;
      } else if (seenPlates.has(licensePlate.toLowerCase())) {
        ok = false;
        error = 'ทะเบียนซ้ำในไฟล์';
      }

      if (ok) {
        seenPlates.add(licensePlate.toLowerCase());
      }

      return {
        rowNumber: index + 2,
        licensePlate,
        province,
        name,
        phone,
        address,
        provinceId,
        ok,
        error,
      };
    });
  }

  private resolveProvinceId(name: string): number {
    if (!name) return 0;
    const exact = this.provinces.find((p) => p.name === name);
    if (exact) return exact.id;
    const lower = name.toLowerCase();
    return this.provinces.find((p) => p.name.toLowerCase() === lower)?.id ?? 0;
  }

  /** ลบช่องว่างทุกชนิด เช่น "1 กก 1234" → "1กก1234" */
  private normalizeLicensePlate(value: string): string {
    return (value ?? '').replace(/\s+/g, '');
  }

  confirmImport(): void {
    const menuId = this.permissionService.getMenuId(this.router.url);
    if (!menuId) {
      this.toast.error('ไม่พบ Menu ID');
      return;
    }
    if (!this.canGoStep3 || this.isImporting) return;

    const payload: ImportParkingCardsPayload = {
      menuId,
      userId: this.userId,
      siteId: this.siteId,
      clientId: this.clientId,
      cardTypeId: this.cardTypeId,
      cardMode: this.cardMode,
      subscriptionId: this.subscriptionId,
      rateId: this.rateId,
      gateGroupId: this.gateGroupId,
      issueDate: this.issueDate,
      expireDate: this.expireDate,
      rows: this.previewRows
        .filter((r) => r.ok)
        .map((r) => ({
          rowNumber: r.rowNumber,
          licensePlate: r.licensePlate,
          province: r.province,
          name: r.name,
          phone: r.phone,
          address: r.address || null,
        })),
    };

    this.isImporting = true;
    this.api.importParkingCards(payload).subscribe({
      next: (res) => {
        this.importResult = this.normalizeResult(res);
        this.isImporting = false;
        if (this.importResult.successCount > 0) {
          this.toast.success(`นำเข้าสำเร็จ ${this.importResult.successCount} รายการ`);
          this.onImported.emit();
        }
        if (this.importResult.failCount > 0) {
          this.toast.error(`ล้มเหลว ${this.importResult.failCount} รายการ`);
        }
      },
      error: (err) => {
        this.isImporting = false;
        this.toast.error(err?.error?.message || err?.error || 'Import ไม่สำเร็จ');
      },
    });
  }

  private normalizeResult(res: ImportParkingCardsResponse | Record<string, unknown>): ImportParkingCardsResponse {
    const raw = res as ImportParkingCardsResponse & Record<string, unknown>;
    const results = (raw.results ?? raw['Results'] ?? []) as ImportParkingCardRowResult[];
    return {
      successCount: Number(raw.successCount ?? raw['SuccessCount'] ?? 0),
      failCount: Number(raw.failCount ?? raw['FailCount'] ?? 0),
      results: results.map((r) => {
        const row = r as ImportParkingCardRowResult & Record<string, unknown>;
        return {
          rowNumber: Number(row.rowNumber ?? row['RowNumber'] ?? 0),
          ok: Boolean(row.ok ?? row['Ok']),
          message: String(row.message ?? row['Message'] ?? ''),
          cardId: (row.cardId ?? row['CardId'] ?? null) as number | null,
          licensePlate: String(row.licensePlate ?? row['LicensePlate'] ?? ''),
          name: String(row.name ?? row['Name'] ?? ''),
        };
      }),
    };
  }

  close(): void {
    this.onClose.emit();
  }
}
